import React from 'react';
import { Formik } from 'formik';
import { cloneDeep, isEmpty } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import Grid from '~/components/core/Atomic/Grid/Grid';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import Text from '~/components/core/TextComponents/Text';
import FieldConfigurationCard from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/FieldConfigurationCard/FieldConfigurationCard';
import type { PaymentsConfiguration } from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/types';
import type { PaymentsConfigurationParams } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

interface GeneralRecoveriesConfigurationDialogProps {
  paymentsConfiguration: PaymentsConfiguration;
  onCancel: () => void;
  onSubmit: (paymentsConfigurationParams: PaymentsConfigurationParams) => Promise<void>;
}

interface RecoveryTypeOption {
  id: string;
  desc: string;
  active: boolean;
}

interface GeneralRecoveriesConfig {
  general_recovery_type: {
    type: string;
    id: string;
    desc: string;
    active: boolean;
    mandatory: boolean;
    options: RecoveryTypeOption[];
  };
}

const GeneralRecoveriesConfigurationDialog: React.FC<GeneralRecoveriesConfigurationDialogProps> = ({
  paymentsConfiguration,
  onCancel,
  onSubmit,
}) => {
  const defaultConfig: GeneralRecoveriesConfig = {
    general_recovery_type: {
      type: 'select',
      id: 'general_recovery_type',
      desc: 'Recovery Type',
      active: true,
      mandatory: false,
      options: [
        { id: 'deductible', desc: 'Deductible', active: true },
        { id: 'subrogation', desc: 'Subrogation', active: true },
        { id: 'salvage', desc: 'Salvage', active: true },
      ],
    },
  };

  const currentConfig: GeneralRecoveriesConfig = !isEmpty(paymentsConfiguration.general_recoveries_configuration)
    ? (paymentsConfiguration.general_recoveries_configuration as GeneralRecoveriesConfig)
    : defaultConfig;

  const handleSubmit = async (values: GeneralRecoveriesConfig) => {
    try {
      const updatedPaymentsConfiguration: PaymentsConfigurationParams = {
        general_recoveries_configuration: values,
      };

      await onSubmit(updatedPaymentsConfiguration);
      onCancel();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    <Formik
      initialValues={cloneDeep(currentConfig)}
      validationSchema={Yup.object().shape({
        general_recovery_type: Yup.object().shape({
          desc: Yup.string().required('Required'),
          options: Yup.array().of(
            Yup.object().shape({
              id: Yup.string().required('Required'),
              desc: Yup.string().required('Required'),
            })
          ),
        }),
      })}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ handleSubmit: formikHandleSubmit, isSubmitting }) => (
        <CardDialog
          isDialog
          onClose={onCancel}
          title="General Recoveries Configuration"
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              disabled={isSubmitting}
              onClickPrimary={() => formikHandleSubmit()}
              onClickSecondary={onCancel}
              primaryLabel="Save Configuration"
              secondaryLabel="Cancel"
            />
          }
        >
          <div className="space-y-6">
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
              Configure the recovery type field that will appear in the general recovery dialog.
            </Text>

            <Grid container spacing={3}>
              <FieldConfigurationCard
                fieldId="general_recovery_type"
                isConfiguredField={false}
                forceDisabled={isSubmitting}
                disableCategories={true}
                disableType={true}
                disableCliveAdditionalInstructions={true}
                withoutRequiredForClaimClosing={true}
                requiredLabel="Required field for recovery submission"
                withRequiredConfirmationModal={false}
              />
            </Grid>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default GeneralRecoveriesConfigurationDialog;
